import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import DashboardLayout from "./components/layout/DashboardLayout";
import LoginPractitionerView from "./views/login/LoginPractitionerView";
import { dashboardViews, pharmaDashboardViews } from "./config";
import { getTenantType } from "./tenant/tenant";
import { applyTenantLanguage } from "./services/i18n";

/**
 * Mapa ruta -> { clinica?, farma? } combinando ambos catalogos de vistas.
 * Constante de modulo (los catalogos son estaticos): un unico set de rutas
 * cubre las 7 de clinica + las 8 de farma; solo "/" existe en ambas, y ahi
 * decide TenantView segun el tipo en cada render (ver mas abajo por que no
 * puede decidirse una sola vez al construir <Routes>).
 */
function buildRouteMap() {
    const map = new Map();
    dashboardViews.forEach((entry) => map.set(entry.path, { ...map.get(entry.path), clinica: entry }));
    pharmaDashboardViews.forEach((entry) => map.set(entry.path, { ...map.get(entry.path), farma: entry }));
    return map;
}
const routeEntries = [...buildRouteMap().entries()];

/**
 * Puerta de acceso a la demo: sin "sesión" (flag en sessionStorage, la pone
 * el login falso al enviar el formulario) redirige al login. Así el login
 * es la pantalla principal al abrir la demo, sin tocar las rutas del dashboard.
 */
function RequireDemoLogin() {
    const loggedIn = sessionStorage.getItem("demoLoggedIn") === "true";
    // Asegura la variante de texto correcta según el tipo de tenant (p.ej. al
    // recargar la página ya con sesión iniciada).
    useEffect(() => {
        if (loggedIn) applyTenantLanguage(getTenantType());
    }, [loggedIn]);
    if (!loggedIn) {
        return <Navigate to="/login" replace />;
    }
    return <DashboardLayout />;
}

/**
 * Resuelve la pantalla segun el tipo de tenant activo EN ESTE RENDER (no al
 * construir <Routes>): clinica y farma tienen catalogos de vistas propios
 * (`config.js`), y el unico path compartido ("/") debe resolver a la vista
 * de cada uno segun corresponda. Leer `getTenantType()` aqui (dentro del
 * componente que React monta al hacer match de la ruta) es lo que garantiza
 * que, tras iniciar sesion, se vea el catalogo correcto: `App()` solo se
 * ejecuta una vez al cargar la pagina (antes de cualquier login), asi que
 * decidir las rutas ahi arriba quedaria fijado con el tenant por defecto.
 * Si el tipo activo no tiene entrada para este path, se vuelve al home.
 */
function TenantView({ clinica, farma }) {
    const type = getTenantType();
    const entry = type === "farma" ? farma : clinica;
    if (!entry) return <Navigate to="/" replace />;
    const Component = (entry.byType && entry.byType[type]) || entry.Component;
    return <Component />;
}

/**
 * Shell del dashboard: layout comun (T-3/T-4) + rutas de las vistas.
 * El login (duplicado visual de mential-webapp, solo demo) va fuera del
 * layout: pantalla completa, sin sidebar/topbar. Es la pantalla de entrada
 * de la demo (ver RequireDemoLogin); cualquier dato enviado "funciona".
 */
function App() {
    return (
        <Routes>
            <Route path="/login" element={<LoginPractitionerView />} />
            <Route element={<RequireDemoLogin />}>
                {routeEntries.map(([path, { clinica, farma }]) => (
                    <Route key={path} path={path} element={<TenantView clinica={clinica} farma={farma} />} />
                ))}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
        </Routes>
    );
}

export default App;
